// Helpers
import { getStatusIconByRole } from 'src/helpers/xt1_helpers/template_helpers';
import { getWebIcon } from 'src/helpers/virtual_helpers/virtual_components_settings_helpers';
// Types
import type { VirtualComponentConfig } from 'src/types/VirtualComponents';

// Regex to determine if a iots condition/action is for a virtual component. Note: id is always +1 in iots
const vc_iots_properties_regex = /(enum|number|button|boolean|text)(20[1-9]|2[1-9][0-9]|300)/;

// Virtual Component "Button" Events
export const VC_BUTTON_PROPS = {
	S: {
		operator: 'short', // Text for Conditions Wizard - Button option
		event: 'one', // Text for the label in Conditions Preview
		icon: 'fak fa-solid-short-pushed',
	},
	SS: {
		operator: 'double_short',
		event: 'double',
		icon: 'fak fa-solid-double-short-pushed',
	},
	SSS: {
		operator: 'triple_short',
		event: 'triple',
		icon: 'fak fa-solid-triple-short-pushed',
	},
	L: {
		operator: 'long',
		event: 'long',
		icon: 'fak fa-solid-long-pushed',
	},
};

/**
 * Maps device webhook event names to scene trigger values.
 * Required for translating XT1 virtual button actions to scene-compatible codes.
 */
export const VC_BUTTON_TRIGGER_EVENTS = {
	single_push: 'S',
	double_push: 'SS',
	triple_push: 'SSS',
	long_push: 'L',
};

/**
 * Get the display properties for a virtual component button event
 *
 * @param event - Event name (S, SS, SSS, L)
 * @returns 	Object with properties for Button Event operator, event, icon
 */
export const getVCButtonEvents = (event: string) => {
	return VC_BUTTON_PROPS[event];
};

/**
 * Check if condition type is for virtual component
 *
 * @param type  - String condition name
 * @returns     - Boolean
 */
export const isVCType = (type: string): boolean => {
	return ['button', 'number', 'boolean', 'enum', 'text'].includes(type);
};

/**
 * Checks if the given IoTS property is a virtual component property.
 * Returns the condition name and channel from iots property string.
 * Example: 'enum201' => { condition_name: 'enum', channel: 201 }
 *
 * @param iots_property - e.g. 'enum201', 'number202', 'button203', 'boolean204', 'text205'
 * @returns  - Object with condition_name and channel or null if
 */
export const extractVCProperty = (
	iots_property: string,
): { condition_name: string; channel: number } | null => {
	let match = iots_property.match(vc_iots_properties_regex);
	if (!match) {
		return null;
	}
	const [, condition_name, channel] = match;
	return {
		condition_name,
		channel: Number(channel),
	};
};

/**
 * Interpretator of Virtual Component Condition iots properties
 * 	Prepares IoTS to UI object for VC condition/action
 * @param iots_property  - The IotS condition/action string to be processed. e.g. 'enum201', 'number202', 'button203', 'boolean204', 'text205'
 * @param tshvalue - The value associated with the condition.
 * @returns  		- An object containing the standardized name, provided value and VC index.
 */
export const interpretIotsVCProperty = (iots_property: string, tshvalue: any): false | any => {
	let match, name, value, component_channel;
	//Extract props from IotS Condition string. NOTE: Channels are always +1 in IotS
	if (!(match = extractVCProperty(iots_property))) {
		return false;
	}
	// Get parameters from the capturing group
	// e.g. number202 -> name='number', channel=201
	name = match.condition_name;
	component_channel = match.channel;

	value = tshvalue;

	// Convert from IoTS format (+1) back to component index
	// e.g. number201 in IoTS means component index 200
	const component_index = component_channel - 1;

	return {
		name,
		value,
		input_index: component_index,
	};
};

/**
 * Retrieves the appropriate FontAwesome icon for a given Virtual Component.
 *
 * @param {VirtualComponentConfig} vcSettings - Virtual Component settings containing role and metadata.
 * @returns {string} - FontAwesome icon class name or an empty string if no match is found.
 */
export const getVCIcon = (vcSettings: VirtualComponentConfig): string => {
	if (vcSettings?.role) {
		return getStatusIconByRole(vcSettings?.role);
	}
	const iconId = Number(vcSettings?.meta?.ui?.webIcon);
	return getWebIcon(iconId)?.icon || '';
};
